/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router-dom'
import { IconButton } from '@mui/material'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import Table, { TableProps } from 'components/Table'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import * as teamMembersClasses from 'pagesNew/TeamMembers/TeamMembersPlatformList/styles'
import { searchByGuideTitle_searchByGuideTitle_items as StudyGuide } from '__generatedTypes__/searchByGuideTitle'
import { FormValues } from 'pagesNew/OrginizeBlogPages/OrganizeCreateSuccessStory/Form/types'
import * as classes from 'pagesNew/OrginizeBlogPages/OrganizeCreateSuccessStory/Form/SuccessStoriesFieldArray/styles'
import { StudyGuidesSearch } from '../StudyGuidesSearch'

export type Props = {
  platform: Platform
  onChange: (name: keyof FormValues, value?: StudyGuide[]) => void
  fields: FieldArrayRenderProps<StudyGuide, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<StudyGuide, HTMLElement>['meta']
  linkParams?: URLSearchParams
}

export const StudyGuidesFieldArray: FC<Props> = ({ platform, fields, meta, onChange, linkParams }) => {
  const { getLink } = useLink()
  const fieldArray = fields.value ?? []

  const handleSort = (newFieldArray: StudyGuide[]) => {
    onChange('articles', newFieldArray)
  }

  const handleAdd = (successStory: StudyGuide) => {
    fields.push(successStory)
  }

  const StudyGuidesColumns: TableProps<StudyGuide>['columns'] = [
    {
      renderCell: (_successStory, index) => (
        <Typography variant="body2" css={classes.rowIndex}>
          {index + 1}
        </Typography>
      ),
      flex: '0 1 30px',
    },
    {
      renderCell: (successStory) => (
        <Typography variant="body2" css={[teamMembersClasses.longText, classes.longText]}>
          {successStory.title}
        </Typography>
      ),
    },
    {
      renderCell: (studyGuide) => (
        <Link to={getLink(cmsRoutes.EDIT_STUDY_GUIDE, { studyGuideId: studyGuide.id }, linkParams)}>
          <IconButton css={teamMembersClasses.editIcon}>
            <EditIcon />
          </IconButton>
        </Link>
      ),
      flex: '0 1 29px',
    },
    {
      renderCell: (_successStory, index: number) => (
        <IconButton onClick={() => fields.remove(index)} css={teamMembersClasses.editIcon}>
          <CrossIcon />
        </IconButton>
      ),
      flex: '0 1 29px',
    },
  ]

  return (
    <div css={classes.root}>
      <Typography variant="body2" css={classes.label}>
        Order of Articles
      </Typography>

      <div css={classes.search}>
        <StudyGuidesSearch
          platform={platform}
          value={fieldArray}
          onAdd={handleAdd}
          error={meta.touched && meta.error}
          formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
        />
      </div>

      {fieldArray.length ? (
        <Table
          css={[teamMembersClasses.table, classes.table]}
          columns={StudyGuidesColumns}
          items={fieldArray}
          hideHeader
          isSortable
          onSort={handleSort}
        />
      ) : (
        <Typography variant="body2" css={classes.emptyLabel}>
          No Articles added
        </Typography>
      )}
    </div>
  )
}
